import { useState } from 'react'
import { Select } from 'antd'
import { useQuery } from '@tanstack/react-query'
import type { ColumnsType } from 'antd/es/table'
import { DataTable } from '@kolab/dms-base/components/ui'
import { PageLayout } from '@kolab/dms-base/components/layout'
import { listResellerCapacity } from '../../api/report.api'
import { pickResellers } from '../../api/lookups.api'
import { reportKeys } from '../../api/queryKeys'
import type { ResellerCapacity } from '../../api/types'
import { StatusBadge } from '../../components/common/StatusBadge'
import { useCursorList } from '../../hooks/useCursorList'
import { CursorListStateView } from '../../components/common/QueryStateView'
import { P, usePermissionStore } from '../../rbac'


export default function ResellerCapacityReport() {
  const [resellerFilter, setResellerFilter] = useState<string | undefined>()
  const { hasPermission } = usePermissionStore()

  const { items, isLoading, isError, error, refetch, isFetchingNextPage, hasNextPage, fetchNextPage } = useCursorList<ResellerCapacity>({
    queryKey: reportKeys.capacity.list({ pageSize: 20, resellerId: resellerFilter }),
    queryFn: (params) => listResellerCapacity(params),
    params: { resellerId: resellerFilter },
    pageSize: 20,
  })

  const resellersQuery = useQuery({
    queryKey: ['lookups', 'resellers'],
    queryFn: () => pickResellers(),
    enabled: hasPermission(P.tenants.reseller.pick),
  })

  const resellerOptions = (resellersQuery.data?.items ?? []).map((r) => ({ label: r.label, value: r.id }))

  const columns: ColumnsType<ResellerCapacity> = [
    { title: 'Sub-license', dataIndex: 'subLicenseKey', key: 'subLicenseKey' },
    {
      title: 'Reseller',
      dataIndex: 'resellerName',
      key: 'resellerName',
    },
    { title: 'Type', dataIndex: 'licenseType', key: 'licenseType' },
    { title: 'Limit', dataIndex: 'deviceLimit', key: 'deviceLimit', align: 'right' as const },
    { title: 'Consumed', dataIndex: 'consumed', key: 'consumed', align: 'right' as const },
    { title: 'Remaining', dataIndex: 'remaining', key: 'remaining', align: 'right' as const },
    { title: 'Expiry', dataIndex: 'expiryDate', key: 'expiryDate' },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (s: string) => <StatusBadge status={s} /> },
  ]

  return (
    <PageLayout
      filters={
        <Select
          allowClear
          placeholder="Filter by reseller"
          style={{ minWidth: 220 }}
          options={resellerOptions}
          value={resellerFilter}
          onChange={(v) => setResellerFilter(v)}
        />
      }
    >
      <CursorListStateView isLoading={isLoading} isError={isError} error={error} refetch={refetch}>
        <DataTable<ResellerCapacity>
          rowKey="resellerLicenseId"
          columns={columns}
          dataSource={items}
          pagination={false}
          infiniteScroll={{ isFetchingNextPage, hasNextPage, fetchNextPage }}
        />
      </CursorListStateView>
    </PageLayout>
  )
}
